/**
 * Environment Detection Utility
 * 
 * This utility provides consistent environment detection across the application
 * to prevent authentication and storage conflicts between different deployment environments.
 */

export type Environment = 'development' | 'staging' | 'production';

export interface EnvironmentInfo {
  name: Environment;
  displayName: string;
  hostname: string;
  apiUrl: string;
  isLocal: boolean;
  storagePrefix: string;
}

/**
 * Detect the current environment based on hostname
 */
export function getCurrentEnvironment(): EnvironmentInfo {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;

  // Development environment (localhost)
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === 'horwathportal.local') {
    return {
      name: 'development',
      displayName: 'Development',
      hostname,
      apiUrl: 'http://localhost:7073',
      isLocal: true,
      storagePrefix: 'dev_',
    };
  }

  // Staging/Test environment
  if (hostname === 'portal-dev.horwathhtlus.com') {
    return {
      name: 'staging',
      displayName: 'Staging',
      hostname,
      apiUrl: 'https://api-dev.horwathhtlus.com',
      isLocal: false,
      storagePrefix: 'staging_',
    };
  }

  // Production environment
  if (hostname === 'portal.horwathhtlus.com') {
    return {
      name: 'production',
      displayName: 'Production',
      hostname,
      apiUrl: 'https://api.horwathhtlus.com',
      isLocal: false,
      storagePrefix: 'prod_',
    };
  }

  // Fallback for unknown environments
  console.warn(`Unknown hostname: ${hostname}, defaulting to development settings`);
  return {
    name: 'development',
    displayName: 'Unknown',
    hostname,
    apiUrl: 'http://localhost:7073',
    isLocal: true,
    storagePrefix: 'unknown_',
  };
}

/**
 * Get environment-specific storage key
 * This prevents conflicts between different environments in the same browser
 */
export function getEnvironmentStorageKey(baseKey: string): string {
  const env = getCurrentEnvironment();
  return `${env.storagePrefix}${baseKey}`;
}

/**
 * Get MSAL cache key with environment prefix
 */
export function getMsalCacheKey(): string {
  const env = getCurrentEnvironment();
  return `msal.${env.storagePrefix}cache`;
}

/**
 * Clear environment-specific localStorage data
 * Useful for logout functionality
 */
export function clearEnvironmentStorage(): void {
  const env = getCurrentEnvironment();
  const keys: string[] = [];
  
  // Collect all keys with the current environment prefix
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(env.storagePrefix)) {
      keys.push(key);
    }
  }
  
  // Remove all environment-specific keys
  keys.forEach(key => {
    localStorage.removeItem(key);
  });
  
  console.log(`Cleared ${keys.length} environment-specific localStorage items for ${env.displayName}`);
}

/**
 * Check if running in development mode
 */
export function isDevelopment(): boolean {
  return getCurrentEnvironment().isLocal;
}

/**
 * Check if running in production
 */
export function isProduction(): boolean {
  return getCurrentEnvironment().name === 'production';
}

/**
 * Get environment display name for UI purposes
 */
export function getEnvironmentDisplayName(): string {
  return getCurrentEnvironment().displayName;
}

/**
 * Debug utility to log current environment info
 * Only logs in development
 */
export function logEnvironmentInfo(): void {
  if (isDevelopment()) {
    const env = getCurrentEnvironment();
    console.log('Environment Info:', {
      name: env.name,
      displayName: env.displayName,
      hostname: env.hostname,
      apiUrl: env.apiUrl,
      isLocal: env.isLocal,
      storagePrefix: env.storagePrefix,
    });
  }
}

// Export singleton instance for convenience
export const environment = getCurrentEnvironment();